import React, { useEffect, useState } from 'react';
import { Box, Button, Spacer } from '@flexisaf/flexibull2';
import { Notify } from '@flexisaf/flexibull2/build/notify';
import FormRender from '@flexisaf/flexi-form-render';

import '@flexisaf/flexi-form-render/dist/index.css';
import request from 'utils/request';
import { processForm, processFormFiles } from 'utils/processFormUtil';
import config from 'utils/config';
import { FormBox } from './styles';
import { FormPreview } from './FormPreview';
import Theme from 'utils/theme';
import { networkErrorHandler } from 'utils/errrorHandlers';

import PropTypes from 'prop-types';

const getRequiredFields = (formSchema = {}) => {
  if (!formSchema) return [];
  if (Object.keys(formSchema).length > 0) {
    const { sections } = formSchema.layout;
    const allColumns = [];
    const formattedCols = [];
    sections.forEach((l) => {
      l.rows.forEach((r) => {
        allColumns.push(r.cols)
      });
    });
    allColumns.forEach((column) => {
      column.forEach((c) => {
        if (c?.props?.required && c?.props?.required === true) {
          formattedCols.push(c.fieldId)
        }
      })
    })
    return formattedCols;
  }
};

const Application = ({
  onClose,
  onSuccess,
  application,
  applicant,
  formId,
  candidateId,
  formItemData,
  formSchema,
  duplicateFields: formDuplicateFields,
}) => {
  if (!application || !formId) {
    onClose();
  }

  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeFormData, setActiveFormData] = useState({});
  const [formValues, setFormValues] = useState({});
  const [duplicateFields, setDuplicateFields] = useState({
    sections: {},
    rows: {},
  });

  const updateFormVal = (key, val) => {
    setFormValues((cur) => ({
        ...cur,
        [key]: val,
      }));
  };

  useEffect(() => {
    setFormValues({ ...formItemData });
    setActiveFormData(formSchema?.schema);
    setDuplicateFields({
      sections: { ...(formDuplicateFields?.sections || {}) },
      rows: { ...(formDuplicateFields?.rows || {}) },
    });
    // eslint-disable-next-line
  }, [formItemData, formSchema, formDuplicateFields]);

  const getPercentageCompletion = (requiredFields, formFields) => {
    const totalFilled = requiredFields.filter((a) => formFields[a]).length;
    if (requiredFields.length === 0) {
      if (Object.keys(formFields).length > 0) return 100;
      return 0;
    }
    return (totalFilled / requiredFields.length) * 100;
  };

  const extractRefereeInfo = (payload) => {
    const keys = Object.keys(payload);
    const refereeFields = keys.filter((key) => key.includes('referee'));
    const filteredList = [];
    if (!refereeFields.length) {
      return [];
    } else {
      refereeFields.forEach((field) => {
        if (field !== 'referee_full_name') {
          if (field !== 'referee_email') {
            if (field !== 'referee_phone') {
              if (field !== 'referee') {
                filteredList.push(field);
              }
            }
          }
        }
      });
      const formattedfilteredList = filteredList.map((list) =>
        list
          .split('-')
          .slice(0, list.split('-').length - 1)
          .join('-')
      );
      const removedDuplicates = [...new Set(formattedfilteredList)];
      const referees = [
        {
          name: payload?.referee_full_name || '',
          email: payload?.referee_email || '',
          phone: payload?.referee_phone || '',
        },
      ];
      removedDuplicates.forEach((d) => {
        referees.push({
          name: payload[`${d}-referee_full_name`] || '',
          email: payload[`${d}-referee_email`] || '',
          phone: payload[`${d}-referee_phone`] || '',
        });
      });
      return referees;
    }
  };

  const submit = async (payload, continueLater = false) => {
    try {
      setSaving(true);
      const attachmentKeys = [];
      const formPayload = {
        applicationKey:
          formValues?.applicationKey ||
          `${candidateId}-${new Date().toISOString()}`,
        percentComplete: getPercentageCompletion(
          getRequiredFields(activeFormData),
          payload
        ),
        submit: !continueLater,
      };
      Object.keys(payload).forEach((p) => {
        if (
          typeof payload[p] !== 'object' ||
          typeof payload[p]?.[0] === 'string' ||
          payload[p] instanceof Date
        ) {
          formPayload[p] = payload[p];
          return;
        }
        if (
          typeof payload[p] === 'object' &&
          p !== null &&
          !payload[p]?.[0]?.saved
        ) {
          if (p !== 'referee') {
            attachmentKeys.push(p);
          }
        }
      });
      formPayload.applicationKey =
        formValues?.applicationKey ||
        `${candidateId}-${new Date().toISOString()}`;
      formPayload.percentComplete = getPercentageCompletion(
        getRequiredFields(activeFormData),
        payload
      );
      formPayload.submit = !continueLater;
      formPayload.duplicateFields = duplicateFields;
      formPayload.referee = extractRefereeInfo(payload);
      const form_data = new FormData();
      form_data.append('data', JSON.stringify(formPayload));
      attachmentKeys.forEach((a) => {
        if (!payload[a][0]) return;
        form_data.append(
          `attachments`,
          payload[a][0],
          `${formPayload.applicationKey}-${a}`
        );
      });
      const submissionURL = `${config.API_BASE_URL}/forms/${applicant.formId}`;
      const persistData = await request({
        method: 'put',
        url: submissionURL,
        data: form_data,
        headers: {
          'Content-Type': `multipart/form-data`,
        },
      });

      if (!persistData.success) {
        networkErrorHandler(persistData?.statusCode, `Unable to update applicant details`);
        return;
      }
      Notify(`Details saved`, {
        position: 'top-right',
        status: 'success',
      });
      setPreview(false);
      onClose();
      setTimeout(() => {
        onSuccess({
          firstName: formPayload?.first_name,
          middleName: formPayload?.middle_name,
          lastName: formPayload?.last_name,
        });
      }, 2000);
    } catch (error) {
    } finally {
      setSaving(false);
    }
  };

  if (!activeFormData?.id) return null;

  if (preview)
    return (
      <FormPreview
        onUpdateForm={() => setPreview(false)}
        onSubmitForm={() => submit(formValues, false)}
        processedForm={processForm({ schema: activeFormData }, duplicateFields)}
        schema={activeFormData}
        processedFormAttachments={processFormFiles({ schema: activeFormData })}
        submissionData={formValues}
        application={application}
        candidateId={candidateId}
        courseId={applicant.course?.id}
        saving={saving}
      />
    );

  const formName =
    activeFormData?.name ||
    activeFormData?.title ||
    application?.title ||
    '';

  return (
    <FormBox>
      <Box margin="0">
        {formName ? (
          <div className="flex-render-form-name">
            <span>Application form</span>
            <h2>{formName}</h2>
          </div>
        ) : null}
        <FormRender
          disableLabels={false}
          styler="wizard"
          data={activeFormData}
          submit={() => setPreview(true)}
          saving={saving}
          fieldValue={formValues}
          setFieldValue={updateFormVal}
          submitTitle="Preview"
          duplicateFields={duplicateFields}
          setDuplicateFields={setDuplicateFields}
          formFiller="applicant"
        />
        <Spacer space="24px" />
      </Box>

      <Box margin="0">
        <Box
          width="100%"
          align="right"
          className="save-continue"
          background="#ffffff"
        >
          <Button
            className="save-button"
            onClick={onClose}
            pad="10px 20px"
            color="transparent"
            bold
            pale
            fontColor={Theme.PrimaryRed}
            height={50}
            spaceLeft="auto"
          >
            Close form
          </Button>
        </Box>
      </Box>
    </FormBox>
  );
};

Application.propTypes = {
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  application: PropTypes.object,
  applicant: PropTypes.object,
  formId: PropTypes.string,
  candidateId: PropTypes.string,
  formItemData: PropTypes.object,
  formSchema: PropTypes.object,
  duplicateFields: PropTypes.object
}

export default Application;
